import React from 'react';
import * as dates from '@simplybenefits/sb-dates';
import { Button, Text, Tooltip, Theme, i18n, tokens, localizationMaps } from '@simplybenefits/sb-ui-library';
import Grid from "@mui/material/Grid";
import { styled } from '@mui/system';
import { useTheme } from '@mui/material/styles';
import AdminSiteCoverageDisplay from './AdminSiteCoverageDisplay';
import { Info as InfoIcon } from "@mui/icons-material";
const StyledHeader = styled('div')(({
  theme
}) => ({
  display: 'flex',
  flex: 1,
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'row',
  backgroundColor: theme.palette.primary.main,
  padding: "10px"
}));

/**
 * @param x
 * @private
 */
function _numberWithCommas(x) {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/**
 * @param x
 * @private
 */
function _parsePhoneNumber(x) {
  return x.replace(/\D+/g, '').replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
}

/**
 * @param handleOnEdit
 * @returns {*}
 * @constructor
 */
const HeaderRow = ({
  onEdit,
  userDivision,
  disableHeaderRow
}) => {
  const theme = useTheme();
  const showEditButton = userDivision.access !== 'Read';
  return /*#__PURE__*/React.createElement(StyledHeader, null, /*#__PURE__*/React.createElement("div", {
    style: {
      flex: 1
    }
  }, /*#__PURE__*/React.createElement(Text, {
    mediumLarge: true,
    bold: true,
    color: Theme.colors.surface
  }, i18n.t(tokens.labels.employees.personalInformation))), disableHeaderRow ? null : showEditButton && /*#__PURE__*/React.createElement(Button, {
    transparent: true,
    color: "primary",
    customStyle: {
      color: '#FFF',
      "&:hover": {
        backgroundColor: theme.palette.primary.dark
      }
    },
    onClick: onEdit
  }, i18n.t(tokens.buttons.editDetails)));
};

/**
 * @param meta
 * @param coverage
 * @param onEdit
 * @param classData
 * @param divisions
 * @returns {JSX.Element}
 * @constructor
 */
const PersonalInfoBlock = ({
  meta,
  coverage,
  onEdit,
  classData,
  divisions,
  isAdminSite,
  disableHeaderRow = false,
  disableBorder = false
}) => {
  const theme = useTheme();
  const userDivision = divisions[meta.divisionID];
  const hasWaived = meta.hasWaived || '';
  const hasStudentID = typeof meta.studentID === 'string';
  const showCoverageInformationLabel = meta.eligibilityOverrideDate || !isAdminSite && meta.hasRefused || Boolean(hasWaived) || !isAdminSite && (Boolean(meta.comparableCoverageEmployer) || Boolean(meta.comparableCoverageCert) || Boolean(meta.comparableCoverageCarrier));
  const styles = {
    container: {
      flex: 1,
      border: disableBorder ? null : "2px solid #F1F1F1",
      borderRadius: disableBorder ? null : 5,
      backgroundColor: '#FFF'
    },
    row: {
      margin: "10px 0",
      padding: "0 20px",
      alignItems: "baseline"
    },
    block: {
      display: 'flex',
      flexWrap: 'wrap',
      flexDirection: 'column'
    },
    effectiveDateDisplay: {
      display: "flex",
      flexDirection: "row",
      alignItems: 'center',
      backgroundColor: theme.palette.primary.light,
      padding: 15,
      width: "95%",
      borderLeft: `3.5px solid ${theme.palette.primary.main}`
    }
  };
  return /*#__PURE__*/React.createElement("div", {
    style: styles.container
  }, /*#__PURE__*/React.createElement(HeaderRow, {
    onEdit: onEdit,
    userDivision: userDivision,
    disableHeaderRow: disableHeaderRow
  }), /*#__PURE__*/React.createElement(Grid, {
    container: true,
    direction: "row",
    alignItems: "center",
    spacing: 2,
    sx: styles.row,
    wrap: "wrap"
  }, /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 6,
    md: 4,
    lg: 3,
    style: {
      padding: 18
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: styles.block
  }, /*#__PURE__*/React.createElement(Text, {
    bold: true,
    small: true
  }, i18n.t(tokens.labels.firstname)), /*#__PURE__*/React.createElement(Text, {
    small: true
  }, meta.firstname))), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 6,
    md: 4,
    lg: 3,
    style: {
      padding: 18
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: styles.block
  }, /*#__PURE__*/React.createElement(Text, {
    bold: true,
    small: true
  }, i18n.t(tokens.labels.lastname)), /*#__PURE__*/React.createElement(Text, {
    small: true
  }, meta.lastname))), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 6,
    md: 4,
    lg: 3,
    style: {
      padding: 18
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: styles.block
  }, /*#__PURE__*/React.createElement(Text, {
    bold: true,
    small: true
  }, i18n.t(tokens.labels.signup.certificateNumber)), /*#__PURE__*/React.createElement(Text, {
    small: true
  }, meta.status === 9 && !isAdminSite ? 'Terminated' : meta.cert || i18n.t(tokens.labels.employees.pending)))), hasStudentID ? /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 6,
    md: 4,
    lg: 3,
    style: {
      padding: 18
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: styles.block
  }, /*#__PURE__*/React.createElement(Text, {
    bold: true,
    small: true
  }, i18n.t(tokens.labels.employees.studyStartDate)), /*#__PURE__*/React.createElement(Text, {
    small: true
  }, dates.getStartOfDay(meta.studyStartDate, 'll', i18n.locale)))) : /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 6,
    md: 4,
    lg: 3,
    style: {
      padding: 18
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: styles.block
  }, /*#__PURE__*/React.createElement(Text, {
    bold: true,
    small: true
  }, i18n.t(tokens.labels.employees.hireDate)), /*#__PURE__*/React.createElement(Text, {
    small: true
  }, dates.getStartOfDay(meta.hireDate, 'll', i18n.locale)))), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 6,
    md: 4,
    lg: 3,
    style: {
      padding: 18
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: styles.block
  }, /*#__PURE__*/React.createElement(Text, {
    bold: true,
    small: true
  }, i18n.t(tokens.labels.employees.enrollmentDate)), /*#__PURE__*/React.createElement(Text, {
    small: true
  }, meta.enrollmentDate ? dates.getStartOfDay(meta.enrollmentDate, 'll', i18n.locale) : i18n.t(tokens.labels.employees.pending)))), !isAdminSite ? /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 6,
    md: 4,
    lg: 3,
    style: {
      padding: 18
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: styles.block
  }, /*#__PURE__*/React.createElement(Text, {
    bold: true,
    small: true
  }, "Late Applicant Date"), /*#__PURE__*/React.createElement(Text, {
    small: true
  }, meta.lateStatusDate ? dates.getStartOfDay(meta.lateStatusDate, 'll') : 'N/A'))) : null, /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 6,
    md: 4,
    lg: 3,
    style: {
      padding: 18
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: styles.block
  }, /*#__PURE__*/React.createElement(Text, {
    bold: true,
    small: true
  }, i18n.t(tokens.titles.division)), divisions[meta.divisionID].description ? /*#__PURE__*/React.createElement(Tooltip, {
    tip: divisions[meta.divisionID].description,
    placement: "top-start"
  }, /*#__PURE__*/React.createElement(Text, {
    small: true
  }, meta.divisionName)) : /*#__PURE__*/React.createElement(Text, {
    small: true
  }, meta.divisionName))), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 6,
    md: 4,
    lg: 3,
    style: {
      padding: 18
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: styles.block
  }, /*#__PURE__*/React.createElement(Text, {
    bold: true,
    small: true
  }, i18n.t(tokens.titles.class)), classData[meta.classID].description ? /*#__PURE__*/React.createElement(Tooltip, {
    tip: classData[meta.classID].description,
    placement: "top-start"
  }, /*#__PURE__*/React.createElement(Text, {
    small: true
  }, meta.className)) : /*#__PURE__*/React.createElement(Text, {
    small: true
  }, meta.className))), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 6,
    md: 4,
    lg: 3,
    style: {
      padding: 18
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: styles.block
  }, /*#__PURE__*/React.createElement(Text, {
    bold: true,
    small: true
  }, i18n.t(tokens.labels.coverageType)), /*#__PURE__*/React.createElement(Text, {
    small: true
  }, localizationMaps.fromEnglish("coverageTypes", meta.coverageType)))), !isAdminSite ? /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 6,
    md: 4,
    lg: 3,
    style: {
      padding: 18
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: styles.block
  }, /*#__PURE__*/React.createElement(Text, {
    bold: true,
    small: true
  }, "Reimbursement Type"), /*#__PURE__*/React.createElement(Text, {
    small: true
  }, meta.requiresCheque ? 'Cheque' : 'EFT'))) : null, /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 6,
    md: 4,
    lg: 3,
    style: {
      padding: 18
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: styles.block
  }, /*#__PURE__*/React.createElement(Text, {
    bold: true,
    small: true
  }, i18n.t(tokens.labels.birthdate)), /*#__PURE__*/React.createElement(Text, {
    small: true
  }, dates.getStartOfDay(meta.birthdate, 'll', i18n.locale)))), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 6,
    md: 4,
    lg: 3,
    style: {
      padding: 18
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: styles.block
  }, /*#__PURE__*/React.createElement(Text, {
    bold: true,
    small: true
  }, i18n.t(tokens.labels.maritalStatus)), /*#__PURE__*/React.createElement(Text, {
    small: true
  }, localizationMaps.fromEnglish("maritalStatuses", meta.maritlStat) || 'N/A'))), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 6,
    md: 4,
    lg: 3,
    style: {
      padding: 18
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: styles.block
  }, /*#__PURE__*/React.createElement(Text, {
    bold: true,
    small: true
  }, i18n.t(tokens.labels.gender)), /*#__PURE__*/React.createElement(Text, {
    small: true
  }, localizationMaps.fromEnglish("genders", meta.gender)))), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 6,
    md: 4,
    lg: 3,
    style: {
      padding: 18
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: styles.block
  }, /*#__PURE__*/React.createElement(Text, {
    bold: true,
    small: true
  }, i18n.t(tokens.labels.phoneNumber)), /*#__PURE__*/React.createElement(Text, {
    small: true
  }, meta.phone ? _parsePhoneNumber(meta.phone) : 'N/A'))), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 6,
    md: 4,
    lg: 3,
    style: {
      padding: 18
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: styles.block
  }, /*#__PURE__*/React.createElement(Text, {
    bold: true,
    small: true
  }, i18n.t(tokens.labels.streetAddress)), /*#__PURE__*/React.createElement(Text, {
    small: true,
    customStyle: {
      wordBreak: 'break-word',
      whiteSpace: 'normal'
    }
  }, meta.addressLineTwo ? ` ${meta.addressLineOne}, ${meta.addressLineTwo} ` : `${meta.addressLineOne || 'N/A'}`))), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 6,
    md: 4,
    lg: 3,
    style: {
      padding: 18
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: styles.block
  }, /*#__PURE__*/React.createElement(Text, {
    bold: true,
    small: true
  }, i18n.t(tokens.labels.account.cityTown)), /*#__PURE__*/React.createElement(Text, {
    small: true
  }, meta.city || 'N/A'))), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 6,
    md: 4,
    lg: 3,
    style: {
      padding: 18
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: styles.block
  }, /*#__PURE__*/React.createElement(Text, {
    bold: true,
    small: true
  }, i18n.t(tokens.labels.province)), /*#__PURE__*/React.createElement(Text, {
    small: true
  }, localizationMaps.fromEnglish("provinces", meta.province)))), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 6,
    md: 4,
    lg: 3,
    style: {
      padding: 18
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: styles.block
  }, /*#__PURE__*/React.createElement(Text, {
    bold: true,
    small: true
  }, "Postal Code"), /*#__PURE__*/React.createElement(Text, {
    small: true
  }, meta.postalCode || 'N/A'))), !hasStudentID ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 6,
    md: 4,
    lg: 3,
    style: {
      padding: 18
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: styles.block
  }, /*#__PURE__*/React.createElement(Text, {
    bold: true,
    small: true
  }, i18n.t(tokens.labels.employees.earningsType)), /*#__PURE__*/React.createElement(Text, {
    small: true
  }, localizationMaps.fromEnglish("earningsTypes", meta.earningsType)))), meta.earningsType === 'Salary' ? /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 6,
    md: 4,
    lg: 3,
    style: {
      padding: 18
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: styles.block
  }, /*#__PURE__*/React.createElement(Text, {
    bold: true,
    small: true
  }, i18n.t(tokens.labels.employees.salary)), /*#__PURE__*/React.createElement(Text, {
    small: true
  }, "$", _numberWithCommas(meta.salary)))) : /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 6,
    md: 4,
    lg: 3,
    style: {
      padding: 18
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: styles.block
  }, /*#__PURE__*/React.createElement(Text, {
    bold: true,
    small: true
  }, i18n.t(tokens.labels.employees.hourlyWage)), /*#__PURE__*/React.createElement(Text, {
    small: true
  }, "$", _numberWithCommas(meta.hourlyWage)))), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 6,
    md: 4,
    lg: 3,
    style: {
      padding: 18
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: styles.block
  }, /*#__PURE__*/React.createElement(Text, {
    bold: true,
    small: true
  }, i18n.t(tokens.labels.employees.hoursPerWeek)), /*#__PURE__*/React.createElement(Text, {
    small: true
  }, meta.hoursPerWeek || 0)))) : null, hasStudentID ? /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 3,
    md: 4,
    lg: 3,
    style: {
      padding: 18
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: styles.block
  }, /*#__PURE__*/React.createElement(Text, {
    bold: true,
    small: true
  }, i18n.t(tokens.labels.employees.studentID)), /*#__PURE__*/React.createElement(Text, {
    small: true
  }, meta.studentID))) : meta.payrollID ? /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 3,
    md: 4,
    lg: 3,
    style: {
      padding: 18
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: styles.block
  }, /*#__PURE__*/React.createElement(Text, {
    bold: true,
    small: true
  }, i18n.t(tokens.labels.employees.payrollID)), /*#__PURE__*/React.createElement(Text, {
    small: true
  }, meta.payrollID))) : null, /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 3,
    md: 4,
    lg: 3,
    style: {
      padding: 18
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: styles.block
  }, /*#__PURE__*/React.createElement(Text, {
    bold: true,
    small: true
  }, i18n.t(tokens.labels.signup.language)), /*#__PURE__*/React.createElement(Text, {
    small: true
  }, meta.language === "fr" ? localizationMaps.fromEnglish("languages", "French") : localizationMaps.fromEnglish("languages", "English")))), hasStudentID ? /*#__PURE__*/React.createElement(React.Fragment, null, meta.degree ? /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 6,
    md: 4,
    lg: 3,
    style: {
      padding: 18
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: styles.block
  }, /*#__PURE__*/React.createElement(Text, {
    bold: true,
    small: true
  }, i18n.t(tokens.labels.employees.degree)), /*#__PURE__*/React.createElement(Text, {
    small: true
  }, meta.degree))) : null) : /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 6,
    md: 4,
    lg: 3,
    style: {
      padding: 18
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: styles.block
  }, /*#__PURE__*/React.createElement(Text, {
    bold: true,
    small: true
  }, i18n.t(tokens.labels.employees.occupation)), /*#__PURE__*/React.createElement(Text, {
    small: true
  }, meta.title))), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 12,
    md: 6,
    lg: 3,
    style: {
      padding: 18
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: styles.block
  }, /*#__PURE__*/React.createElement(Text, {
    bold: true,
    small: true
  }, i18n.t(tokens.labels.email)), /*#__PURE__*/React.createElement(Text, {
    small: true,
    customStyle: {
      wordBreak: 'break-all'
    }
  }, meta.email))), !isAdminSite ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 6,
    md: 4,
    lg: 3,
    style: {
      padding: 18
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: styles.block
  }, /*#__PURE__*/React.createElement(Text, {
    bold: true,
    small: true
  }, "Smoker Status"), /*#__PURE__*/React.createElement(Text, {
    small: true
  }, meta.isSmoker ? "Smoker" : "Non Smoker"))), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 6,
    md: 4,
    lg: 3,
    style: {
      padding: 18
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: styles.block
  }, /*#__PURE__*/React.createElement(Text, {
    bold: true,
    small: true
  }, "Dependent Smoker Status"), /*#__PURE__*/React.createElement(Text, {
    small: true
  }, meta.hasDependentSmoker ? "Smoker" : "Non Smoker")))) : null, meta.paymentType !== null ? /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 12,
    md: 6,
    lg: 3,
    style: {
      padding: 18
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: styles.block
  }, /*#__PURE__*/React.createElement(Text, {
    bold: true,
    small: true
  }, i18n.t(tokens.labels.employees.memberPaymentType)), /*#__PURE__*/React.createElement(Text, {
    small: true
  }, meta.paymentType === "CC" ? i18n.t(tokens.labels.banking.creditCard) : i18n.t(tokens.labels.employees.eft)))) : null), meta.enrollmentDate && isAdminSite && /*#__PURE__*/React.createElement(AdminSiteCoverageDisplay, {
    coverage: coverage,
    hasWaived: hasWaived,
    meta: meta
  }), /*#__PURE__*/React.createElement(Grid, {
    container: true,
    direction: "row",
    alignItems: "center",
    spacing: 2,
    sx: styles.row,
    wrap: "wrap"
  }, meta.enrollmentDate ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 6,
    md: 4,
    lg: 3,
    style: {
      padding: 18
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: styles.block
  }, /*#__PURE__*/React.createElement(Text, {
    bold: true,
    small: true
  }, i18n.t(tokens.titles.waivedCoverage)), hasWaived ? /*#__PURE__*/React.createElement(Text, {
    small: true
  }, hasWaived === 'Health-Dental' ? i18n.t(tokens.titles.healthAndDental) : hasWaived) : /*#__PURE__*/React.createElement(Text, {
    small: true
  }, i18n.t(tokens.buttons.no)))), showCoverageInformationLabel ? /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 12,
    style: {
      paddingRight: 18,
      paddingLeft: 18,
      paddingTop: 18
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: styles.block
  }, /*#__PURE__*/React.createElement(Text, {
    veryBold: true,
    medium: true
  }, "Coverage Information:"))) : null, meta.eligibilityOverrideDate ? /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 12
  }, /*#__PURE__*/React.createElement("div", {
    style: styles.effectiveDateDisplay
  }, /*#__PURE__*/React.createElement(InfoIcon, {
    "aria-label": "info",
    style: {
      fontSize: 20,
      color: theme.palette.primary.main,
      marginRight: 10
    }
  }), /*#__PURE__*/React.createElement(Text, {
    AvMedium: true,
    customStyle: {
      marginRight: 6.5
    }
  }, i18n.t(tokens.labels.employees.coverageInformation)), /*#__PURE__*/React.createElement(Text, {
    bold: true,
    AvMedium: true
  }, dates.getStartOfDay(meta.eligibilityOverrideDate, "ll", i18n.locale)))) : null, !isAdminSite && meta.hasRefused ? /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 3,
    md: 4,
    lg: 3,
    style: {
      padding: 18
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: styles.block
  }, /*#__PURE__*/React.createElement(Text, {
    bold: true,
    small: true
  }, "Refused Coverage"), meta.hasRefused ? /*#__PURE__*/React.createElement(Text, {
    small: true
  }, meta.hasRefused === 'Health-Dental' ? 'Health and Dental' : meta.hasRefused) : /*#__PURE__*/React.createElement(Text, {
    small: true
  }, "No"))) : null, Boolean(hasWaived) && /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 3,
    md: 4,
    lg: 3,
    style: {
      padding: 18
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: styles.block
  }, /*#__PURE__*/React.createElement(Text, {
    bold: true,
    small: true
  }, i18n.t(tokens.titles.waivedCoverageFor)), meta.waiveFor === 'Self-Dependents' ? /*#__PURE__*/React.createElement(Text, {
    small: true
  }, i18n.t(tokens.labels.employees.memberAndDependents)) : /*#__PURE__*/React.createElement(Text, {
    small: true
  }, i18n.t(tokens.labels.signup.dependentsOnly)))), !isAdminSite ? /*#__PURE__*/React.createElement(React.Fragment, null, Boolean(meta.comparableCoverageEmployer) && /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 3,
    md: 4,
    lg: 3,
    style: {
      padding: 18
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: styles.block
  }, /*#__PURE__*/React.createElement(Text, {
    bold: true,
    small: true
  }, "Comparable Coverage Organization"), /*#__PURE__*/React.createElement(Text, {
    small: true
  }, meta.comparableCoverageEmployer))), Boolean(meta.comparableCoverageCarrier) && /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 3,
    md: 4,
    lg: 3,
    style: {
      padding: 18
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: styles.block
  }, /*#__PURE__*/React.createElement(Text, {
    bold: true,
    small: true
  }, "Comparable Coverage Carrier"), /*#__PURE__*/React.createElement(Text, {
    small: true
  }, meta.comparableCoverageCarrier))), Boolean(meta.comparableCoverageCert) && /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 3,
    md: 4,
    lg: 3,
    style: {
      padding: 18
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: styles.block
  }, /*#__PURE__*/React.createElement(Text, {
    bold: true,
    small: true
  }, "Comparable Coverage Certificate"), /*#__PURE__*/React.createElement(Text, {
    small: true
  }, meta.comparableCoverageCert)))) : null) : null));
};
export default PersonalInfoBlock;